import Link from 'next/link';
import { useEffect, useState } from 'react';
import { BreadcrumbsAndTitle } from './BreadcrumbsAndTitle';
import { PolicySection } from './TermsAndConditionsPage';
import { clearGuestIdentity, clearMetaAdvancedMatching } from '@util/analytics';

interface PrivacyPolicyPageProps {
  content?: string;
  title?: string;
}

const GF_DO_NOT_SELL_KEY = 'gf_do_not_sell';

const PrivacyPolicyPage = ({}: PrivacyPolicyPageProps) => {
  const lastUpdatedTs = 'April 14, 2026';

  const [optedOut, setOptedOut] = useState(false);
  const [gpcActive, setGpcActive] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [storageBlocked, setStorageBlocked] = useState(false);

  useEffect(() => {
    try {
      setOptedOut(localStorage.getItem(GF_DO_NOT_SELL_KEY) === 'true');
      setGpcActive((navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true);
    } catch {
      // localStorage or navigator may be blocked in private browsing or by browser policy
    }
    setMounted(true);
  }, []);

  const handleOptOut = () => {
    let optOutSaved = false;
    try {
      localStorage.setItem(GF_DO_NOT_SELL_KEY, 'true');
      setOptedOut(true);
      setStorageBlocked(false);
      // F2 / GB #1373: cancels any still-pending Advanced Matching retry.
      // Does NOT clear an already-attached pixel identity — Phase A-E wire
      // experiments (2026-07-15, live on gearfocus.com) proved Meta's pixel
      // is FIRST-WRITE-WINS and there is no in-page clear (see
      // clearMetaAdvancedMatching's doc comment in analytics.service.ts).
      // The hard reload below is what actually honors the opt-out for a
      // pixel that already has identity attached.
      clearMetaAdvancedMatching();
      optOutSaved = true;
    } catch {
      setStorageBlocked(true);
    }
    // TASK-1373 / GB #1206: the opt-out itself already blocks the guest
    // identity from ever transmitting (every consumer re-checks
    // isDoNotSellSignalActive), but there's no reason to leave the
    // plaintext email sitting in sessionStorage after the user has opted
    // out — clear it too. Unconditional (unlike the reload below): this is
    // a plain synchronous cache clear, not dependent on the localStorage
    // write having succeeded.
    clearGuestIdentity();

    // Phase A-E wire evidence: a hard reload is the only way to guarantee
    // this tab's pixel stops carrying whatever identity was attached before
    // the opt-out. Unconditional on success (not gated on
    // isPixelIdentityAttached() the way the cross-tab listener below is) —
    // this is a deliberate, explicit user action (they clicked "Opt Out"),
    // so forcing a reload to guarantee the opt-out actually took effect is
    // acceptable UX here, unlike a background tab getting silently reloaded
    // out from under the user. Only runs if the localStorage write actually
    // succeeded — a failed write means the opt-out isn't in effect yet
    // (see the storageBlocked messaging below), so there is nothing to
    // reload for.
    if (optOutSaved) {
      window.location.reload();
    }
  };

  const handleOptIn = () => {
    try {
      localStorage.removeItem(GF_DO_NOT_SELL_KEY);
      setOptedOut(false);
      setStorageBlocked(false);
    } catch {
      setStorageBlocked(true);
    }
  };

  const isEffectivelyOptedOut = optedOut || gpcActive;

  return (
    <>
      <div className="mb-[6rem] w-full max-w-default px-[1.6rem] sm:px-default-padding-x">
        <BreadcrumbsAndTitle
          items={[
            { label: 'Home', href: '/' },
            {
              label: 'Privacy Policy',
              href: '#',
              current: true,
              currentCss: 'text-brand-black',
            },
          ]}
          title="Privacy Policy"
        />
        <p className="text-[12px] italic">Effective Date {lastUpdatedTs}</p>
          <div className="flex flex-col gap-[1.6rem] py-[2rem]">
            <PolicySection title="1. Scope">
              This Policy applies to all use of GearFocus.com and related services, 
              including our website, mobile applications, and APIs (collectively, 
              the &quot;Services&quot;). It covers registered users (&quot;Members&quot;) and visitors 
              who browse the platform (&quot;Guests&quot;).
            </PolicySection>
            <PolicySection title="2. Children Under the Age of 18">
              Our Services are intended for individuals aged 18 and older. We do not knowingly collect data from children under 18. If we learn of such collection, we will delete the data promptly.
            </PolicySection>
            <PolicySection title="3. Information We Collect">
              <p>We collect personal information to provide and improve our Services, including:</p>

              <div>
                <b className="block mt-[2rem] font-semibold">A. Information You Provide Directly</b>
                <ul className="ml-[1rem]">
                  <li><b className="font-semibold">Personal Details:</b> Name, email, phone number, and shipping address.</li>
                  <li><b className="font-semibold">Account Information:</b> Username, password, and preferences.</li>
                  <li><b className="font-semibold">Payment Details:</b> Billing address and payment methods through secure third-party processors.</li>
                  <li><b className="font-semibold">Seller Details:</b> Taxpayer information, business registration, and payout preferences.</li>
                  <li><b className="font-semibold">Seller Verification Profiles:</b> Social media handles, eBay usernames, website URLs, and other online profile links you voluntarily provide during seller onboarding or in your account settings. Providing this information is optional and helps us verify your presence in the photography and filmmaking community. You can update or remove these at any time in your Shop Settings.</li>
                  <li><b className="font-semibold">Support Interactions:</b> Communications with customer service.</li>
                </ul>

                <b className="block mt-[2rem] font-semibold">B. Information We Collect Automatically</b>
                <ul className="ml-[1rem]">
                  <li><b className="font-semibold">Device Information:</b> IP address, browser type, operating system, and device identifiers.</li>
                  <li><b className="font-semibold">Usage Data:</b> Pages visited, clicks, time spent, and search history.</li>
                  <li><b className="font-semibold">Cookies & Tracking:</b> Data from cookies and similar technologies (see Section 9).</li>
                </ul>

                <b className="block mt-[2rem] font-semibold">C. Information from Third Parties</b>
                <ul className="ml-[1rem]">
                  <li><b className="font-semibold">Payment Providers:</b> Transaction details and billing addresses.</li>
                  <li><b className="font-semibold">Marketing Partners:</b> Insights from advertising interactions.</li>
                  <li><b className="font-semibold">Seller Verification Sources:</b> Seller verification is what makes GearFocus a trusted marketplace. We use industry-standard identity and address verification services to confirm seller information. For some verifications, our service providers may reference publicly available information to confirm identity. This data is processed solely for verification, never retained for other purposes, and never sold.</li>
                </ul>

                <b className="block mt-[2rem] font-semibold">D. Information from Non-Account Holders (Gear Match)</b>
                <p className="mt-[0.5rem]">
                  When you use our Gear Match compatibility tool, we may collect the following information even if you do not have a GearFocus account:
                </p>
                <ul className="ml-[1rem] mt-[0.5rem]">
                  <li><b className="font-semibold">Equipment Information:</b> The camera body or lens model you select to find compatible gear listings.</li>
                  <li><b className="font-semibold">Email Address (optional):</b> If you choose to receive notifications when compatible gear becomes available, we collect the email address you provide.</li>
                  <li><b className="font-semibold">Technical Information:</b> Your IP address, browser type, and device identifiers collected automatically when you use the tool (as described in Section 3B above).</li>
                </ul>
                <p className="mt-[1rem]"><b className="font-semibold">How we use this information:</b></p>
                <ul className="ml-[1rem] mt-[0.5rem]">
                  <li>To display compatible gear listings that match the equipment you selected.</li>
                  <li>If you provide your email address and consent to notifications, to send you email alerts when new compatible listings become available. These emails are delivered through Omnisend, our email marketing platform.</li>
                  <li>In aggregate, anonymized form, to understand which camera systems our visitors use and to improve our marketplace inventory.</li>
                </ul>
                <p className="mt-[1rem]"><b className="font-semibold">How long we keep this information:</b></p>
                <ul className="ml-[1rem] mt-[0.5rem]">
                  <li>If you do not provide an email address or do not consent to marketing notifications, your equipment selection data is retained for 90 days and then permanently deleted.</li>
                  <li>If you consent to notifications, your equipment and email data is retained until you unsubscribe or request deletion, whichever comes first.</li>
                </ul>
                <p className="mt-[1rem]"><b className="font-semibold">Who receives this information:</b></p>
                <ul className="ml-[1rem] mt-[0.5rem]">
                  <li><b className="font-semibold">Omnisend</b> (email delivery platform) &mdash; receives your email address and equipment preferences only if you opt in to notifications.</li>
                  <li><b className="font-semibold">Google Analytics (GA4)</b> &mdash; receives anonymized event data about Gear Match usage (camera model selected, whether a notification was requested). This data is not linked to your email address.</li>
                  <li>No other third parties receive your Gear Match data.</li>
                </ul>
                <p className="mt-[1rem]"><b className="font-semibold">Your rights:</b></p>
                <ul className="ml-[1rem] mt-[0.5rem]">
                  <li>You may request deletion of your Gear Match data at any time by emailing <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a> with the subject line &quot;Gear Match Data Deletion Request.&quot;</li>
                  <li>California residents: This data is not sold or shared for cross-context behavioral advertising. See the California and Québec Privacy Rights Addendum below for your full California privacy rights.</li>
                  <li>Canadian residents: Your email address will only be used for marketing notifications if you provide express consent by checking the notification opt-in box. You may withdraw consent at any time by unsubscribing. Unsubscribe requests will be honored within 10 business days.</li>
                </ul>
              </div>
            </PolicySection>
            <PolicySection title="4. How We Use Your Information">
              <p>We use your information to:</p>
              <ul className="ml-[1rem]">
                <li>Facilitate transactions and manage your account.</li>
                <li>Provide personalized services and recommendations.</li>
                <li>Improve our platform and develop new features.</li>
                <li>Communicate service-related messages (e.g., order updates).</li>
                <li>Ensure security and prevent fraud.</li>
                <li>Comply with legal and regulatory obligations.</li>
              </ul>
            </PolicySection>
            <PolicySection title="5. Sharing Your Information">
              <p>We only share personal data when necessary:</p>
              <ul className="ml-[1rem]">
                <li><b className="font-semibold">Service Providers:</b> Payment processors, shipping carriers, and marketing platforms.</li>
                <li><b className="font-semibold">Legal Compliance:</b> In response to legal obligations, investigations, or law enforcement requests.</li>
                <li><b className="font-semibold">Business Transfers:</b> If we merge or are acquired, data may be transferred as part of the transaction.</li>
              </ul>
            </PolicySection>

            <PolicySection title="6. Your Choices & Rights">
              <b className="block font-semibold">Manage Your Data</b>
              <ul className="ml-[1rem]">
                <li><b className="font-semibold">Account Settings:</b> Update or delete your personal details through your account dashboard.</li>
                <li><b className="font-semibold">Marketing Preferences:</b> Opt out of promotional emails via the unsubscribe link.</li>
                <li><b className="font-semibold">Cookies:</b> Adjust cookie settings through your browser at any time.</li>
              </ul>
              <b className="block mt-[2rem] font-semibold">International Users</b>
            <p>If you live outside the U.S., you may have rights to access, correct, delete, or restrict processing of your data. Contact <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a> to exercise these rights.</p>
            </PolicySection>

            <PolicySection title="7. Data Security">
              <p>We use encryption, firewalls, and secure data storage to protect your information. However, no system can guarantee complete security. Use strong passwords and maintain account security.</p>
            </PolicySection>

            <PolicySection title="8. Data Retention">
              <p>We keep your data as long as needed for business, legal, or compliance reasons. After account closure, we securely delete or anonymize your information based on applicable laws.</p>
              <p className="mt-[1rem]">We delete seller verification records 30 days after your application is decided. Social media profiles and other information you voluntarily provide remain in your account until you remove them through your Shop Settings.</p>
            </PolicySection>

            <PolicySection title="9. Cookies & Tracking Technologies">
              <p>We use cookies to:</p>
              <ul className="ml-[1rem]">
                <li>Recognize returning visitors.</li>
                <li>Save your preferences and personalize services.</li>
                <li>Improve site performance and marketing efforts.</li>
              </ul>
              <br />
              <p>When you use our Services, we may use automatic data collection technologies to deliver a better experience. For more details, see our <Link href="/policies/cookies" className="text-brand-secondary underline">Cookies Policy</Link>.</p>
            </PolicySection>

            <PolicySection title="10. International Data Transfers">
              <p>Your data may be transferred to servers outside your country, including the U.S. We use safeguards like contractual agreements to ensure your data stays protected.</p>
            </PolicySection>

            <PolicySection title="11. Policy Updates">
              <p>We may update this Policy periodically. Major changes will be communicated through notifications on our platform or via email.</p>
            </PolicySection>

            <PolicySection title="12. Contact Us">
              <p>For privacy-related inquiries, contact:</p>
              <p>Email: <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a></p>
              <p>Mailing Address: GearFocus, 10903 Cade Cir, Austin, TX 78726</p>
            </PolicySection>

            <PolicySection title="California and Québec Privacy Rights Addendum">
              <p className="italic">Effective Date: April 1, 2026</p>

              <b className="block mt-[2rem] font-semibold">Section A: Your California Privacy Rights</b>
              <p className="mt-[1rem]">
                If you are a California resident, you have the right to request that GearFocus disclose the categories
                and specific pieces of personal information we have collected about you, the categories of sources from
                which that information was collected, the business or commercial purposes for collecting, using,
                disclosing, or sharing that information, and the categories of third parties to whom we disclose that
                information. You also have the right to request deletion of personal information we collected from you,
                subject to exceptions, and the right to request correction of inaccurate personal information.
              </p>
              <p className="mt-[1rem]">
                GearFocus does not sell personal information for money. GearFocus may share certain personal information
                with advertising partners for cross-context behavioral advertising, ad measurement, audience matching,
                and campaign attribution. California residents have the right to opt out of that sharing.
              </p>
              <p className="mt-[1rem]">
                California residents may submit requests to know, delete, or correct by emailing{' '}
                <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a>.
                California residents may opt out of the sharing of personal information by using the opt-out
                control in <a href="#your-choices" className="text-brand-secondary underline">Section A.5</a>{' '}
                below, or by enabling a recognized opt-out preference signal, such as Global Privacy Control,
                where required by California law.
              </p>
              <p className="mt-[1rem]">
                We will not discriminate against you for exercising your California privacy rights.
              </p>

              <b className="block mt-[2rem] font-semibold">A.1 Categories of Personal Information We Collect</b>
              <ul className="ml-[1rem] mt-[0.5rem]">
                <li><b className="font-semibold">Identifiers:</b> Name, email, phone, address, IP address, username, device identifiers.</li>
                <li><b className="font-semibold">Customer Records:</b> Name, address, phone, payment details (collected by Stripe).</li>
                <li><b className="font-semibold">Commercial Information:</b> Purchase history, products viewed, cart contents, offers made, seller records.</li>
                <li><b className="font-semibold">Internet/Network Activity:</b> Browsing history, search queries, pages visited, clicks, referral URLs, browser type.</li>
                <li><b className="font-semibold">Approximate Geolocation:</b> City/state/country derived from IP address, shipping and billing addresses.</li>
                <li><b className="font-semibold">Account Credentials:</b> Hashed passwords and authentication data.</li>
                <li><b className="font-semibold">Sensitive Personal Information:</b> Seller tax ID and payout/bank information (used only for seller onboarding, payments, tax reporting, fraud prevention, and legal compliance).</li>
              </ul>

              <b className="block mt-[2rem] font-semibold">A.2 How We Use Your Information</b>
              <ul className="ml-[1rem] mt-[0.5rem]">
                <li>Facilitating transactions between buyers and sellers.</li>
                <li>Account creation and management.</li>
                <li>Customer support and communications.</li>
                <li>Platform improvement and new feature development.</li>
                <li>Security, fraud prevention, and legal compliance.</li>
                <li>Advertising and marketing.</li>
              </ul>

              <b className="block mt-[2rem] font-semibold">A.3 Categories of Third Parties</b>
              <ul className="ml-[1rem] mt-[0.5rem]">
                <li>Payment processors and payout providers.</li>
                <li>Shipping, logistics, and fulfillment providers.</li>
                <li>Email delivery and marketing providers.</li>
                <li>Analytics, advertising, and attribution partners.</li>
                <li>Hosting, search, content, and infrastructure providers.</li>
                <li>Security and error monitoring providers.</li>
                <li>Tax compliance providers.</li>
              </ul>

              <b className="block mt-[2rem] font-semibold">A.4 Categories Shared for Advertising</b>
              <p className="mt-[0.5rem]">
                In the preceding 12 months, GearFocus may have shared identifiers, customer records, commercial
                information, internet/network activity, and approximate geolocation with advertising partners. GearFocus
                does not knowingly sell or share personal information of consumers under 16 years of age.
              </p>

              {/* No scroll-mt here. globals.css sets scroll-padding-top on the
                  scrolling element for every anchor target on the site, and
                  scroll-margin ADDS to it rather than replacing it — the
                  14rem this used to carry (140px at the 10px root) stacked on
                  top of the header offset and landed this heading ~300px past
                  where it should. The global rule is the only offset now. */}
              <b id="your-choices" className="block mt-[2rem] font-semibold">A.5 Right to Opt Out</b>
              <p className="mt-[0.5rem]">
                You may opt out using the control below, or by enabling Global Privacy Control (GPC) in your
                browser. When opted out: Meta pixel tracking is suppressed, TIE/Revenue Roll visitor
                identification is suppressed, server-side Meta CAPI tags are blocked, and Google Ads Enhanced
                Conversions user matching is suppressed. Opting out does not affect your ability to use
                GearFocus, GA4 analytics, transactional emails, or account security.
              </p>

              <div className="mt-[1.6rem] rounded-lg border border-gray-200 bg-gray-50 p-[1.6rem]">
                <p className="font-semibold mb-[0.8rem]">Data Sharing with Meta (Facebook / Instagram)</p>
                <p className="text-[1.4rem] mb-[1.2rem]">
                  When you opt out below, GearFocus will stop sharing your browsing data with Meta for
                  advertising purposes. This does not affect your ability to buy, sell, or use any GearFocus
                  features.
                </p>

                <div className="min-h-[4rem]" aria-live="polite">
                  {mounted ? (
                    <>
                      {gpcActive && (
                        <div className="mb-[1rem] rounded-md border border-blue-200 bg-blue-50 px-[1.2rem] py-[0.8rem] text-[1.3rem] text-blue-800">
                          Your browser has Global Privacy Control (GPC) enabled. Meta pixel sharing is automatically suppressed.
                        </div>
                      )}

                      {isEffectivelyOptedOut ? (
                        <div className="flex flex-col gap-[1rem] sm:flex-row sm:items-center">
                          <div className="flex items-center gap-[0.6rem] rounded-md border border-green-200 bg-green-50 px-[1.2rem] py-[0.8rem] text-[1.3rem] font-medium text-green-800">
                            <svg className="h-[1.4rem] w-[1.4rem] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            You have opted out of data sharing
                          </div>
                          {!gpcActive && (
                            <button
                              type="button"
                              onClick={handleOptIn}
                              className="text-[1.3rem] text-gray-500 underline hover:text-gray-700"
                            >
                              Opt back in
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleOptOut}
                          className="rounded-md bg-[#1B1B1B] px-[1.6rem] py-[0.9rem] text-[1.3rem] font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#1B1B1B] focus:ring-offset-2 transition-opacity"
                        >
                          Opt Out of Data Sharing
                        </button>
                      )}

                      {storageBlocked && (
                        <div className="mt-[1rem] rounded-md border border-amber-200 bg-amber-50 px-[1.2rem] py-[0.8rem] text-[1.3rem] text-amber-900">
                          We couldn&apos;t save your preference in this browser (private browsing or storage may
                          be blocked), so your opt-out is not yet in effect. To complete it, please email{' '}
                          <a href="mailto:support@gearfocus.com" className="font-medium underline">support@gearfocus.com</a>
                          {' '}and we will honor your request manually, or enable Global Privacy Control in your
                          browser, which we honor automatically.
                        </div>
                      )}
                    </>
                  ) : (
                    <div aria-hidden="true" className="h-[4rem] w-full animate-pulse rounded-md bg-gray-200" />
                  )}
                </div>
              </div>

              <b className="block mt-[2rem] font-semibold">A.6 Sensitive Personal Information</b>
              <p className="mt-[0.5rem]">
                GearFocus uses sensitive personal information only for permitted purposes (payment processing, tax
                compliance, fraud prevention, seller onboarding, account security). Because of that, GearFocus does not
                offer a separate &quot;Limit the Use of My Sensitive Personal Information&quot; mechanism.
              </p>

              <b className="block mt-[2rem] font-semibold">A.7 How to Submit a Request</b>
              <ul className="ml-[1rem] mt-[0.5rem]">
                <li>Email: <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a></li>
                <li>Phone: 877-606-3504</li>
                <li>Mail: GearFocus, 10903 Cade Cir, Austin, TX 78726</li>
              </ul>
              <p className="mt-[0.5rem]">
                We verify identity before processing requests. We will respond within 45 days (up to 90 days with
                notice). Authorized agents are accepted with proof of authorization.
              </p>

              <b className="block mt-[2rem] font-semibold">A.8 Non-Discrimination</b>
              <p className="mt-[0.5rem]">
                GearFocus will not deny services, charge different prices, or provide different quality of service for
                exercising your California privacy rights.
              </p>

              <b className="block mt-[2rem] font-semibold">Section B: Data Retention</b>
              <ul className="ml-[1rem] mt-[0.5rem]">
                <li><b className="font-semibold">Account data:</b> While active, plus 3 years after account closure.</li>
                <li><b className="font-semibold">Orders and transactions:</b> 7 years.</li>
                <li><b className="font-semibold">Seller tax information:</b> 7 years.</li>
                <li><b className="font-semibold">Support communications:</b> 3 years.</li>
                <li><b className="font-semibold">Marketing data:</b> Until unsubscribe or deletion request.</li>
                <li><b className="font-semibold">Gear Match guest data (no marketing consent):</b> 90 days.</li>
                <li><b className="font-semibold">Gear Match consent records:</b> 3 years after last notification sent or unsubscribe.</li>
                <li><b className="font-semibold">Analytics data:</b> 14 months.</li>
                <li><b className="font-semibold">Error logs:</b> 90 days.</li>
                <li><b className="font-semibold">Server/CDN logs:</b> Short period per provider settings.</li>
              </ul>

              <b className="block mt-[2rem] font-semibold">Section C: Security and Breach Notification</b>
              <p className="mt-[0.5rem]">
                GearFocus uses reasonable safeguards to protect your personal information. If a breach requiring notice
                occurs, we will provide notice as required by applicable law.
              </p>

              <b className="block mt-[2rem] font-semibold">Section D: Global Privacy Control</b>
              <p className="mt-[0.5rem]">
                GearFocus honors Global Privacy Control (GPC) signals where required by applicable law. When a GPC
                signal is detected, we treat it as a request to opt out of sharing personal information for cross-context
                behavioral advertising.
              </p>

              <b className="block mt-[2rem] font-semibold">Section E: Named Service Providers and Partners</b>
              <ul className="ml-[1rem] mt-[0.5rem]">
                <li><b className="font-semibold">Payments and payouts:</b> Stripe, Stripe Connect, Affirm.</li>
                <li><b className="font-semibold">Shipping and logistics:</b> ShipStation, ShipEngine.</li>
                <li><b className="font-semibold">Transactional email:</b> SendGrid.</li>
                <li><b className="font-semibold">Marketing email:</b> Omnisend.</li>
                <li><b className="font-semibold">Analytics and advertising:</b> Google Analytics, Google Ads, Meta, Microsoft Ads, TIE/Revenue Roll, Stape.</li>
                <li><b className="font-semibold">Infrastructure:</b> Firebase, Firestore, BunnyCDN, Algolia, Plasmic.</li>
                <li><b className="font-semibold">Security and monitoring:</b> Sentry.</li>
                <li><b className="font-semibold">Tax compliance:</b> TaxJar.</li>
              </ul>

              <b className="block mt-[2rem] font-semibold">Québec Privacy Rights</b>
              <p className="mt-[0.5rem]">
                If you are located in Québec, GearFocus handles your personal information in accordance with applicable
                Québec privacy law. You may request access to and correction of your personal information, and withdraw
                consent where processing is based on consent. Questions, requests, or complaints may be directed to the
                Chief Executive Officer at{' '}
                <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a>.
              </p>

              <b className="block mt-[2rem] font-semibold">Vos droits en matière de protection des renseignements personnels au Québec</b>
              <p className="mt-[0.5rem]">
                Si vous êtes situé au Québec, GearFocus traite vos renseignements personnels conformément aux lois
                applicables du Québec en matière de protection de la vie privée. Vous pouvez demander l&apos;accès aux
                renseignements personnels que nous détenons à votre sujet et demander la rectification de renseignements
                inexacts, sous réserve des limites prévues par la loi. Vous pouvez également retirer votre consentement
                lorsque le traitement est fondé sur le consentement, sous réserve des restrictions légales ou
                contractuelles applicables.
              </p>
              <p className="mt-[0.5rem]">
                Pour toute question, demande ou plainte concernant les renseignements personnels, vous pouvez communiquer
                avec le chef de la direction à{' '}
                <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a>.
              </p>
            </PolicySection>

          </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
